import {useMemo} from 'react';
import type {DataLoadStatus} from 'api/types';
import {mapRtkToDataLoad} from 'store/api';
import {useAppSelector} from 'store/hooks';
import type {FullManifestation} from '../types';
import {
  manifestationApi,
  useGetManifestationsByEansQuery,
} from '../manifestationApi';

// All args select the same cache entry, see ManifestationsCache
const selectManifestations =
  manifestationApi.endpoints.getManifestationsByEans.select([]);

const noEans: string[] = [];

const useStableEans = (eans: string[]): string[] => {
  const key = [...eans].sort().join(',');
  return useMemo(() => (key ? key.split(',') : noEans), [key]);
};

/**
 * Loads the manifestations of the given eans. Manifestations already loaded,
 * e.g. for another set of eans, are taken from cache, and only the missing
 * eans are fetched.
 * Skipped when no eans are given.
 */
export const useManifestationsByEans = (
  eans: string[] | undefined,
): DataLoadStatus<FullManifestation[]> => {
  const requestedEans = useStableEans(eans ?? noEans);
  const cache = useAppSelector(selectManifestations).data;

  const missingEans = useStableEans(
    requestedEans.filter(ean => !cache?.loadedEans.includes(ean)),
  );

  const {status, error} = useGetManifestationsByEansQuery(missingEans, {
    skip: requestedEans.length === 0,
  });

  const manifestations = useMemo(() => {
    if (!cache) {
      return undefined;
    }

    return requestedEans
      .map(ean => cache.manifestationsByEan[ean])
      .filter(manifestation => manifestation !== undefined);
  }, [cache, requestedEans]);

  return useMemo(
    () =>
      requestedEans.length
        ? mapRtkToDataLoad(status, manifestations, error)
        : {status: 'NotLoaded'},
    [requestedEans.length, status, manifestations, error],
  );
};
